import type { Contract } from '@paid-ui/models/contract';
import type { Address } from '@paid-ui/schemas/zod/address';

import { sum } from 'lodash';

import { formatAddress } from '@paid-ui/components/place-autocomplete';
import { postExecutedContractState, VariationStage } from '@paid-ui/constants';
import { ClaimMethod } from '@paid-ui/enums/claim';
import {
  ContractClass,
  ContractState,
  ContractTemplate,
  ContractType,
} from '@paid-ui/enums/contract';
import { SupercontractBrand, SupercontractCode } from '@paid-ui/enums/supercontract';
import { WorkItemStatus } from '@paid-ui/enums/work-item';

import { utc2Mel } from './datetime';

/**
 * Get total construction days of contract.
 *
 * @param contract Current contract
 */
export function getConstructionDays(contract?: Contract) {
  if (!contract) return 0;

  const {
    contractType,
    paymentStructure,
    variations = [],
    variationWorkItems = [],
    constructionDays = 0,
    initialConstructionDays = 0,
  } = contract;

  if (constructionDays) {
    return constructionDays;
  }

  const totalTimeExtension =
    paymentStructure === ClaimMethod.PERIODIC
      ? variationWorkItems.reduce(
          (result, { workItemStatus, timeExtension }) =>
            result +
            (workItemStatus &&
            [WorkItemStatus.APPROVED, WorkItemStatus.COMPLETED].includes(workItemStatus)
              ? (timeExtension?.days ?? 0)
              : 0),
          0,
        )
      : variations.reduce(
          (result, { variationStage, reasonableDelayDays = 0 }) =>
            result +
            (variationStage &&
            [VariationStage.APPROVED, VariationStage.COMPLETED].includes(variationStage)
              ? reasonableDelayDays
              : 0),
          0,
        );

  switch (contractType) {
    case ContractType.FUNDING_CONTRACT:
    case ContractType.HEAD_CONTRACT: {
      const {
        inclementWeather = 0,
        weekendDays = 0,
        weekdayPublicHolidays = 0,
        rosteredDaysOff = 0,
        foreseeableBreaks = 0,
        builderHolidays = 0,
        additionalDaysFromVariations = 0,
        reasonableDelays,
      } = contract;

      return sum([
        inclementWeather,
        weekendDays,
        weekdayPublicHolidays,
        rosteredDaysOff,
        foreseeableBreaks,
        builderHolidays,
        additionalDaysFromVariations,
        reasonableDelays?.days ?? 0,
        totalTimeExtension,
        initialConstructionDays,
      ]);
    }

    case ContractType.SUBCONTRACT:
    case ContractType.SUPPLY_CONTRACT:
    case ContractType.SERVICE_CONTRACT: {
      return sum([totalTimeExtension, initialConstructionDays]);
    }

    default: {
      return 0;
    }
  }
}

/**
 * Get completion date
 *
 * @param contract - Contract
 * @returns completed at
 */
export function getCompletionDate(contract?: Contract) {
  if (!contract?.commencementDate) return null;
  const { actualCompletionDate, anticipatedCompletionDate, commencementDate } = contract;

  if (actualCompletionDate) {
    return utc2Mel(actualCompletionDate);
  } else if (anticipatedCompletionDate) {
    return utc2Mel(anticipatedCompletionDate);
  }

  const constructionDays = getConstructionDays(contract);
  return utc2Mel(commencementDate).add(constructionDays, 'day');
}

/**
 * Get completion date
 *
 * @param contract - Contract
 * @param dateFormat - date format
 * @returns completed at
 */
export function getCompletionDateInFormat(contract?: Contract, dateFormat = 'D MMM YYYY') {
  const date = getCompletionDate(contract);
  return date ? date.format(dateFormat) : '-';
}

export const getFullContractName = (contractName: string, address: Address | null = null) => {
  return address
    ? [contractName, formatAddress(address)].filter(Boolean).join(' · ')
    : contractName;
};

interface ContractLikeObject {
  hasAllSigned?: boolean;
  contractState?: ContractState | '';
  [key: string]: any;
}

export const checkIfContractIsExecuted = (contract: ContractLikeObject) => {
  return (
    !!contract.hasAllSigned &&
    !!contract.contractState &&
    postExecutedContractState.has(contract.contractState)
  );
};

/**
 * Templates that contract-service executes straight from party acceptance (`signing_flow: NONE`).
 * There are no signatories, no signing session and no PDF: the digital contract is the contract.
 */
const digitalOnlyTemplateCodes: ReadonlySet<string> = new Set([
  SupercontractCode.PAID_SUB_STAGED_2026,
  SupercontractCode.PAID_SUB_PERIODIC_2026,
  SupercontractCode.PAID_SUPPLY_STAGED_2026,
  SupercontractCode.PAID_SUPPLY_PERIODIC_2026,
]);

export const isDigitalOnlyContract = (templateCode?: string | null) => {
  return !!templateCode && digitalOnlyTemplateCodes.has(templateCode);
};

/**
 * Whether web-app can still review, amend or finish drafting the contract. Super contracts and
 * legacy on-platform head contracts can. The legacy Paid sub/supply contracts and the legacy
 * off-platform contracts lost their review and amend endpoints (PWA-16232, PWA-16233), so old
 * records of those can only be viewed.
 */
export const hasContractChangeFlow = (contract: {
  contractTemplate?: ContractTemplate | '' | null;
  contractType?: ContractType | '' | null;
}) => {
  const { contractTemplate, contractType } = contract;
  if (contractTemplate === ContractTemplate.SUPER_CONTRACT) return true;
  return (
    contractType === ContractType.HEAD_CONTRACT &&
    !contractTemplate?.startsWith(ContractTemplate.OFF_PLATFORM)
  );
};

export const templateCodeToUrl = (templateCode?: string) => {
  const prefix = 'OFF_PLATFORM';

  if (!templateCode) {
    return '';
  }

  if (templateCode.startsWith(prefix)) {
    return [prefix, ...templateCode.replace(`${prefix}_`, '').split('_')].join('/');
  }

  return templateCode.split('_').join('/');
};

export const templateCodeToBrand = (
  templateCode?: SupercontractCode,
  contractClass?: ContractClass,
) => {
  if (!templateCode) {
    switch (contractClass) {
      case ContractClass.ONPLATFORM_PRIME_RBN: {
        return SupercontractBrand.RBN;
      }
      case ContractClass.ONPLATFORM_SUB_PAID:
      case ContractClass.ONPLATFORM_SUPPLY_PAID: {
        return SupercontractBrand.PAID;
      }
      default: {
        return SupercontractBrand.UNSPECIFIED;
      }
    }
  }
  if (templateCode.startsWith('ABIC')) {
    return SupercontractBrand.ABIC;
  }
  if (templateCode.startsWith('AIA')) {
    return SupercontractBrand.AIA;
  }
  if (templateCode.startsWith('MBA')) {
    return SupercontractBrand.MBA;
  }
  if (templateCode.startsWith('PAID')) {
    return SupercontractBrand.PAID;
  }
  if (templateCode.startsWith('RBN')) {
    return SupercontractBrand.RBN;
  }
  return SupercontractBrand.UNSPECIFIED;
};

export const isContractPreAccepted = (state: ContractState | '') =>
  [ContractState.SUBMITTED, ContractState.RESUBMITTED, ContractState.REJECTED].includes(state);

export const getReviewerTooltip = (isContractPostExecuted: boolean) =>
  isContractPostExecuted
    ? 'This party had the ability to review and amend the contract before signing.'
    : 'This party can review the contract and make amendments.';
